import { useEffect, useRef, useState } from "react";
import { IconAlertTriangle, IconDownload, IconXCircle } from "./icons";

export type ExportBannerStatus = "exporting" | "ready" | "error" | "idle";

export interface ExportBannerProps {
  status: ExportBannerStatus;
  onCancel: () => void;
  errorMessage?: string;
  progress?: { total: number; exported: number };
  downloadUrl?: string;
}

const ExportSpinner = () => (
  <div
    className="h-5 w-5 animate-spin rounded-full border-2 border-[#b4c0ff] border-t-[#283a99]"
    role="img"
    aria-label="loading spinner"
  />
);

const CancelButton = ({ onCancel }: { onCancel: () => void }) => (
  <button
    type="button"
    className="flex items-center gap-1 text-sm text-[#741414]"
    onClick={onCancel}
  >
    <IconXCircle variant="sm" color="#741414" />
    Cancel
  </button>
);

const DismissButton = ({ onDismiss }: { onDismiss: () => void }) => (
  <button
    type="button"
    className="flex items-center gap-1 text-sm text-[#741414]"
    onClick={onDismiss}
  >
    <IconXCircle variant="sm" color="#741414" />
    Dismiss
  </button>
);

export const ExportBanner = ({
  status,
  onCancel,
  errorMessage,
  progress,
  downloadUrl,
}: ExportBannerProps) => {
  const [downloaded, setDownloaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (downloaded) {
      timerRef.current = setTimeout(() => onCancel(), 5000);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [downloaded, onCancel]);

  useEffect(() => {
    if (status !== "ready") {
      setDownloaded(false);
    }
  }, [status]);

  if (status === "idle") return null;

  if (status === "error") {
    return (
      <div
        role="status"
        className="flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-4 py-2.5 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <IconAlertTriangle variant="sm" color="#AD1A1A" />
          <span className="text-sm text-red-900">
            {errorMessage || "Export failed"}
          </span>
        </div>
        <DismissButton onDismiss={onCancel} />
      </div>
    );
  }

  if (status === "ready" && downloadUrl) {
    return (
      <div
        role="status"
        className="flex items-center justify-between rounded-md border border-[#364ecc] bg-[#ecefff] px-4 py-2.5 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-black">Export ready</span>
          <a
            href={downloadUrl}
            className="flex items-center gap-1 text-sm font-medium text-[#364ecc] underline"
            onClick={() => setDownloaded(true)}
          >
            <IconDownload variant="sm" color="#364ecc" />
            Download
          </a>
        </div>
        <DismissButton onDismiss={onCancel} />
      </div>
    );
  }

  const message =
    progress && progress.total > 0
      ? `Exporting ${progress.exported} of ${progress.total}...`
      : "Export in progress, this may take a moment";

  return (
    <div
      role="status"
      className="flex items-center justify-between rounded-md border border-[#364ecc] bg-[#ecefff] px-4 py-2.5 shadow-sm"
    >
      <div className="flex items-center gap-2">
        <ExportSpinner />
        <span className="text-sm text-black">{message}</span>
      </div>
      <CancelButton onCancel={onCancel} />
    </div>
  );
};
